import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { getSocialProviderIcon } from '@/components/auth/social-provider-icons'
import { getEnabledSocialProviders, socialAuthEnabled } from '@/constants/auth-social'
import { startSocialAuth } from '@/lib/social-auth'
import { cn } from '@/lib/utils'

interface SocialAuthButtonsProps {
  signupRole?: string | null
  disabled?: boolean
  className?: string
}

export function SocialAuthButtons({ signupRole, disabled, className }: SocialAuthButtonsProps) {
  const providers = getEnabledSocialProviders()
  const [error, setError] = useState('')
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)

  if (!socialAuthEnabled() || providers.length === 0) {
    return null
  }

  const roleRequired = signupRole !== undefined
  const canStart = !roleRequired || Boolean(signupRole)

  const handleClick = async (providerId: string) => {
    setError('')
    setLoadingProvider(providerId)
    try {
      const result = await startSocialAuth(providerId, {
        signupRole: roleRequired ? signupRole : null,
      })
      if (!result.success && result.error) {
        setError(result.error)
        setLoadingProvider(null)
      }
    } catch {
      setError('Network error. Please try again.')
      setLoadingProvider(null)
    }
  }

  return (
    <div className={cn('grid gap-3', className)}>
      {error && (
        <div className="p-3 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10">
          {error}
        </div>
      )}
      {providers.map((provider) => {
        const Icon = getSocialProviderIcon(provider.id)
        const isLoading = loadingProvider === provider.id

        return (
          <Button
            key={provider.id}
            type="button"
            variant="outline"
            className="h-12 w-full justify-center gap-2 font-medium"
            disabled={disabled || !canStart || loadingProvider !== null}
            onClick={() => void handleClick(provider.id)}
          >
            {Icon && !isLoading ? <Icon className="h-5 w-5 shrink-0" /> : null}
            {isLoading ? 'Redirecting…' : provider.label}
          </Button>
        )
      })}
    </div>
  )
}
